import { Divider, List, Menu, Table, Text } from '@mantine/core';
import { IconAlertCircle, IconEdit, IconTrash } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DeleteModal } from '../../../components/DeleteModal';
import { TableRowMenu } from '../../../components/TableRowMenu';
import { useModal } from '../../../hooks/useModal';
import { defaultIconStyle } from '../../../theme/styling/iconStyling';
import { mutate } from '../../../util/api/mutate';
import { undefinedCheck } from '../../../util/undefinedCheck';
import type { BranchDto, NewBranchDto } from '../types/BranchTypes';
import { EditBranchModal } from './EditBranchModal';
import { KeyedRow } from '../../../components/KeyedRow';

interface BranchRowProps {
	initialBranch: BranchDto;
	refreshTable: () => void;
}

export function BranchRow({ initialBranch, refreshTable }: BranchRowProps) {
	const { organisation } = useParams();

	const [openTimings, setOpenTimings] = useState(false);

	const editModal = useModal();
	const deleteModal = useModal();

	const [branch, setBranch] = useState(initialBranch);

	const deleteMutation = mutate(
		`${organisation}/branches/delete/${branch.branchId}`,
	);

	const editBranch = useMemo(() => {
		const mappedBranch: NewBranchDto = {
			branchId: branch.branchId,
			name: branch.name,
			addressLineOne: branch.addressLineOne,
			addressLineTwo: branch.addressLineTwo,
			town: branch.town,
			phone: branch.phone,
			email: branch.email,
			postCode: branch.postCode,
			county: branch.county,
			website: branch.website,
			area: branch.area,
		};

		return mappedBranch;
	}, [branch]);

	const updateValues = (editBranch: NewBranchDto) => {
		setBranch({
			...branch,
			name: editBranch.name,
			addressLineOne: editBranch.addressLineOne,
			addressLineTwo: editBranch.addressLineTwo,
			town: editBranch.town,
			email: editBranch.email,
			postCode: editBranch.postCode,
			phone: editBranch.phone,
			county: editBranch.county,
			website: editBranch.website,
			area: editBranch.area,
		});
	};

	return (
		<>
			<DeleteModal
				close={deleteModal.close}
				isOpened={deleteModal.isOpen}
				titleType={'Branch'}
				itemDescription={branch.name}
				mutation={deleteMutation}
				onComplete={refreshTable}
				extraInfo={
					<>
						<Divider p={0} pb={22} />
						<Text size={'1rem'} fw={500} pb={10}>
							Warning&nbsp;
							<IconAlertCircle size={'0.7rem'} />
						</Text>
						<Text pb={10}>
							Deleting a branch will also remove some items or their
							relationships that could cause unexpected issues:
						</Text>
						<List>
							<List.Item>Branch Stocks</List.Item>
							<List.Item>Promotions</List.Item>
							<List.Item>POS Messages</List.Item>
							<List.Item>Operators</List.Item>
							<List.Item>Suppliers</List.Item>
							<List.Item>Certain Sales Information</List.Item>
							<List.Item>Account Branch Associations</List.Item>
							<List.Item>Rear Displays Branch Associations</List.Item>
							<List.Item>Customer Associations</List.Item>
						</List>
					</>
				}
			/>
			<EditBranchModal
				updateValueInTable={updateValues}
				close={editModal.close}
				opened={editModal.isOpen}
				editBranch={editBranch}
			/>
			<KeyedRow>
				<Table.Td>{undefinedCheck(branch.name)}</Table.Td>
				<Table.Td>{undefinedCheck(branch.addressLineOne)}</Table.Td>
				<Table.Td>{undefinedCheck(branch.area)}</Table.Td>
				<Table.Td>{undefinedCheck(branch.town)}</Table.Td>
				<Table.Td>{undefinedCheck(branch.county)}</Table.Td>
				<Table.Td>{undefinedCheck(branch.postCode)}</Table.Td>
				<Table.Td>{undefinedCheck(branch.phone)}</Table.Td>
				<Table.Td>{undefinedCheck(branch.email)}</Table.Td>
				<Table.Td>
					<TableRowMenu>
						<Menu.Item
							leftSection={<IconEdit {...defaultIconStyle} />}
							onClick={() => editModal.open()}
							data-cy={'edit-button'}
						>
							Edit
						</Menu.Item>
						<Menu.Item
							leftSection={<IconTrash {...defaultIconStyle} />}
							c={'red'}
							onClick={() => deleteModal.open()}
						>
							Delete
						</Menu.Item>
					</TableRowMenu>
				</Table.Td>
			</KeyedRow>
		</>
	);
}
